import React, { useState, useEffect } from 'react';
import '../CSS/vendorform.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ENDPOINTS } from '../Helper/endpoints';
// import Cookies from 'js-cookie';
import Footer from "./Footer";

const getCookie = (cookieName) => {
  const cookies = document.cookie.split('; ');
  for (let cookie of cookies) {
    const [name, value] = cookie.split('=');
    if (name === cookieName) return decodeURIComponent(value);
  }
  return null;
};

const VendorForm = ({ setCurrentPage }) => {
  const [formData, setFormData] = useState({
    name: '', address: '', GSTIN: '', pan_card_no: '', aadhar_no: '', mobile: '',
    email: '', account_holder_name: '', account_number: '', IFSC_code: '', bank_name: '',
    branch: '', UPI_ID: '', organization_pid: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const getOrganizationsPid = () => {
    const userSessionData = getCookie('userSession');
    if (userSessionData) {
      try {
        const parsedSessionData = JSON.parse(userSessionData);
        return parsedSessionData.data.user_info.organizations_pid;
      } catch (e) {
        console.error('Error parsing organizations_pid from cookie:', e);
        return null;
      }
    }
    return null;
  };
  
  useEffect(() => {
    const pid = getOrganizationsPid();
    console.log('Organizations PID:', pid);
    setFormData((prevData) => ({ ...prevData, organization_pid: pid || '', }));
  }, []);

  // Extract PAN from GSTIN
  const extractPanFromGstin = (gstin) => {
    if (gstin && gstin.length >= 12) {
      return gstin.substring(2, 12);
    }
    return '';
  };

  // Format numbers with spaces every 4 digits
  const formatWithSpaces = (value, blockSize = 4) => {
    // Remove all spaces first
    const cleanValue = value.replace(/\s/g, '');
    // Add spaces after every blockSize characters
    const formattedValue = cleanValue.replace(new RegExp(`(.{${blockSize}})(?=.)`, 'g'), '$1 ');
    return formattedValue;
  };

  const validateField = (name, value) => {
    let error = '';
    
    if (name === 'GSTIN') {
      if (value) {
        // Updated regex to accept the example GSTIN: 27ASQPC7826G1ZG
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(value)) error = 'Invalid GSTIN format';
        
        // Check if PAN in GSTIN matches PAN field
        const panFromGstin = extractPanFromGstin(value);
        if (panFromGstin && formData.pan_card_no && panFromGstin !== formData.pan_card_no) {
          error = 'PAN number in GSTIN does not match PAN field';
        }
      }
    }
    
    if (name === 'pan_card_no') {
      if (value) {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(value)) error = 'Invalid PAN format';
        
        // Check if PAN matches the one in GSTIN
        const panFromGstin = extractPanFromGstin(formData.GSTIN);
        if (panFromGstin && value && panFromGstin !== value) {
          error = 'PAN number does not match with GSTIN';
        }
      }
    }
    
    if (name === 'UPI_ID') {
      if (value) {
        const upiRegex = /^[a-zA-Z0-9.\-_]{2,}@[\w]{3,}$/;
        if (!upiRegex.test(value)) error = 'Invalid UPI ID format';
      }
    }
      
    if (name === 'account_number') {
      const cleanValue = value.replace(/\s/g, '');
      if (!/^\d*$/.test(cleanValue)) {
        error = 'Account number should contain only digits';
      } else if (cleanValue.length < 9 || cleanValue.length > 18) {
        error = 'Account number must be 9 to 18 digits';
      }
    }
    
    if (name === 'aadhar_no') {
      const cleanValue = value.replace(/\s/g, '');
      if (!/^\d*$/.test(cleanValue)) {
        error = 'Aadhaar number should contain only digits';
      } else if (cleanValue.length !== 0 && cleanValue.length !== 12) {
        error = 'Aadhaar number must be 12 digits';
      }
    }
    
    if (name === 'mobile') {
      if (!/^\d*$/.test(value)) {
        error = 'Mobile number should contain only digits';
      } else if (value.length !== 0 && value.length !== 10) {
        error = 'Mobile number must be 10 digits';
      }
    }
    
    if (name === 'IFSC_code') {
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscRegex.test(value)) error = 'Invalid IFSC code format';
    }
    
    return error;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle GSTIN change - auto-fill PAN
    if (name === 'GSTIN') {
      const panFromGstin = extractPanFromGstin(value);
      
      // Update both GSTIN and PAN fields
      setFormData((prevData) => ({
        ...prevData,
        GSTIN: value,
        pan_card_no: panFromGstin // Auto-fill PAN
      }));
      
      // Validate both fields
      const gstinError = validateField('GSTIN', value);
      const panError = validateField('pan_card_no', panFromGstin);
      
      setErrors((prevErrors) => ({
        ...prevErrors,
        GSTIN: gstinError,
        pan_card_no: panError
      }));
      
      return;
    }
    
    // Handle special input formatting
    if (name === 'mobile') {
      // Ensure only digits are entered
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      const error = validateField(name, digitsOnly);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
      setFormData((prevData) => ({ ...prevData, [name]: digitsOnly }));
      return;
    }
    
    if (name === 'aadhar_no') {
      // Ensure only digits are entered and format with spaces
      const digitsOnly = value.replace(/\D/g, '').slice(0, 12);
      const formattedValue = formatWithSpaces(digitsOnly);
      const error = validateField(name, formattedValue);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
      setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
      return;
    }
    
    if (name === 'account_number') {
      // Ensure only digits are entered and format with spaces
      const digitsOnly = value.replace(/\D/g, '').slice(0, 18);
      const formattedValue = formatWithSpaces(digitsOnly);
      const error = validateField(name, formattedValue);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
      setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
      return;
    }
    
    if (name === 'pan_card_no') {
      // Handle PAN field change - validate against GSTIN if present
      const error = validateField(name, value);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
      setFormData((prevData) => ({ ...prevData, [name]: value }));
      
      // Add cross-validation with GSTIN
      if (formData.GSTIN) {
        const panFromGstin = extractPanFromGstin(formData.GSTIN);
        if (panFromGstin && value && panFromGstin !== value) {
          setErrors((prevErrors) => ({ 
            ...prevErrors, 
            pan_card_no: 'PAN number does not match with GSTIN' 
          }));
        }
      }
      return;
    }
    
    // Default handling for other fields
    const error = validateField(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Validation for errors
    const newErrors = Object.keys(formData).reduce((acc, field) => {
      const error = validateField(field, formData[field]);
      if (error) acc[field] = error;
      return acc;
    }, {});

    // Additional validation to ensure PAN and GSTIN match
    if (formData.GSTIN && formData.pan_card_no) {
      const panFromGstin = extractPanFromGstin(formData.GSTIN);
      if (panFromGstin !== formData.pan_card_no) {
        newErrors.GSTIN = 'PAN number in GSTIN does not match PAN field';
        newErrors.pan_card_no = 'PAN number does not match with GSTIN';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      Swal.fire({
        icon: 'error', title: 'Validation Error', text: 'Please fix the errors before submitting.',
        customClass: { popup: 'small-swal-popup' }, showConfirmButton: false, timer: 1500,
      }); return;
    }
    setIsLoading(true);
    // Ensure empty GSTIN, PAN, Aadhaar, and UPI ID are sent as "NA"
    const updatedFormData = {
      ...formData,
      // Remove spaces from numeric fields before submission
      aadhar_no: formData.aadhar_no.replace(/\s/g, '').trim() === '' ? 'NA' : formData.aadhar_no.replace(/\s/g, ''),
      account_number: formData.account_number.replace(/\s/g, ''),
      GSTIN: formData.GSTIN.trim() === '' ? 'NA' : formData.GSTIN,
      pan_card_no: formData.pan_card_no.trim() === '' ? 'NA' : formData.pan_card_no,
      UPI_ID: formData.UPI_ID.trim() === '' ? 'NA' : formData.UPI_ID,
    };
    axios.post(ENDPOINTS.VENDOR, updatedFormData, { headers: { 'Content-Type': 'application/json' }, })
      .then(() => {
        Swal.fire({
          icon: 'success', title: 'Success!', text: 'Vendor registered successfully.',
          customClass: { popup: 'small-swal-popup' }, showConfirmButton: false, timer: 1500,
        });
        setFormData({
          name: '', address: '', GSTIN: '', pan_card_no: '', aadhar_no: '',
          mobile: '', email: '', account_holder_name: '', account_number: '', IFSC_code: '',
          bank_name: '', branch: '', UPI_ID: '', organization_pid: formData.organization_pid,
        });
        setErrors({});
        setTimeout(() => {
          console.log('Navigating to VendorList...');
          localStorage.setItem('currentPage', 'Dashboard');
          setCurrentPage('VendorList');
        }, 1500);
      })
      .catch((error) => {
        Swal.fire({
          icon: 'error', title: 'Error!', text: error.response?.data?.message || 'Something went wrong. Please try again.',
          customClass: { popup: 'small-swal-popup' }, showConfirmButton: false, timer: 1500,
        });
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  return (
    <>
      <div className="vendor-list">
        <form onSubmit={handleSubmit}>
          <i className="bi bi-arrow-left-circle-fill back-icon"
            onClick={() => { setCurrentPage("VendorList"); }}
            style={{ cursor: "pointer",  display: "inline-block", width: "24px",            
              height: "24px" }}></i>
          <h2>Vendor Registration Form</h2>
          <div className="row mb-3">
            <div className="col-sm-12 col-md-6">
              <input type="text" name="name" className="form-control form-control-lg" placeholder="Enter Name"
                value={formData.name || ''} onChange={handleChange} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <input type="text" name="GSTIN" className="form-control form-control-lg" placeholder="Enter GSTIN"
                value={formData.GSTIN || ''} onChange={handleChange} maxLength="15" />
              {errors.GSTIN && (<small className="text-danger">{errors.GSTIN}</small>)}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-12 col-md-6">
              <input type="text" name="pan_card_no" className="form-control form-control-lg" placeholder="Enter Pan card no"
                value={formData.pan_card_no || ''} onChange={handleChange} />
              {errors.pan_card_no && (<small className="text-danger">{errors.pan_card_no}</small>)}
            </div>
            <div className="col-sm-12 col-md-6">
              <input type="text" name="aadhar_no" className="form-control form-control-lg" placeholder="Enter Aadhar no"
                value={formData.aadhar_no || ''} onChange={handleChange} />
              {errors.aadhar_no && (<small className="text-danger">{errors.aadhar_no}</small>)}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-12 col-md-6">
              <input type="text" name="mobile" className="form-control form-control-lg" placeholder="Enter Mobile (digits only)"
                value={formData.mobile || ''} onChange={handleChange} required />
              {errors.mobile && (<small className="text-danger">{errors.mobile}</small>)}
            </div>
            <div className="col-sm-12 col-md-6">
              <input type="text" name="UPI_ID" className="form-control form-control-lg" placeholder="Enter UPI ID"
                value={formData.UPI_ID || ''} onChange={handleChange} />
              {errors.UPI_ID && (<small className="text-danger">{errors.UPI_ID}</small>)}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-12 col-md-6">
              <input type="email" name="email" className="form-control form-control-lg" placeholder="Enter Email"
                value={formData.email || ''} onChange={handleChange} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <input
                type="text" name="account_holder_name" className="form-control form-control-lg" placeholder="Enter Account holder name"
                value={formData.account_holder_name || ''} onChange={handleChange} required />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-12 col-md-6">
              <input type="text" name="account_number" className="form-control form-control-lg" placeholder="Enter Account number (digits only)"
                value={formData.account_number || ''} onChange={handleChange} required />
              {errors.account_number && (<small className="text-danger">{errors.account_number}</small>)}
            </div>
            <div className="col-sm-12 col-md-6">
              <select name="bank_name" className="form-control form-control-lg" value={formData.bank_name || ''}
                onChange={handleChange} required >
                <option value="">Select Bank Name</option>
                <option value="Axis Bank">Axis Bank</option>
                <option value="Bank of Baroda">Bank of Baroda</option>
                <option value="Bank of India">Bank of India</option>
                <option value="Bank of Maharashtra">Bank of Maharashtra</option>
                <option value="Canara Bank">Canara Bank</option>
                <option value="Central Bank of India">Central Bank of India</option>
                <option value="HDFC Bank">HDFC Bank</option>
                <option value="ICICI Bank">ICICI Bank</option>
                <option value="IDBI Bank">IDBI Bank</option>
                <option value="Indian Bank">Indian Bank</option>
                <option value="Indian Overseas Bank">Indian Overseas Bank</option>
                <option value="IndusInd Bank">IndusInd Bank</option>
                <option value="Kotak Mahindra Bank">Kotak Mahindra Bank</option>
                <option value="Punjab and Sind Bank">Punjab and Sind Bank</option>
                <option value="Punjab National Bank">Punjab National Bank</option>
                <option value="State Bank of India (SBI)">State Bank of India (SBI)</option>
                <option value="UCO Bank">UCO Bank</option>
                <option value="Union Bank of India">Union Bank of India</option>
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-12 col-md-6">
              <input type="text" name="branch" className="form-control form-control-lg" placeholder="Enter Branch"
                value={formData.branch || ''} onChange={handleChange} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <input
                type="text" name="IFSC_code" className="form-control form-control-lg" placeholder="Enter IFSC code"
                value={formData.IFSC_code || ''} onChange={handleChange} required />
              {errors.IFSC_code && (<small className="text-danger">{errors.IFSC_code}</small>)}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col">
              <input type="text" name="address" className="form-control form-control-lg" placeholder="Enter Address"
                value={formData.address || ''} onChange={handleChange} required />
            </div>
          </div>
          <div className="text-center">
            <button type="submit" className="submit-btn-invoice" disabled={isLoading}>
              {isLoading ? 'Submitting...' : 'Submit'} </button>
          </div>
        </form>
      </div>
      <Footer />
    </>
  );
};
export default VendorForm;